import { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Users, Clock, MapPin, MessageSquare, UserMinus, XCircle, Loader2, Share2, Check, Zap } from 'lucide-react';

const STATE_LABEL = { confirmed: 'Confirmed', waitlist: 'Waitlist' };
const STATUS_BADGE = {
  open: { label: 'Open', cls: 'bg-[#56dfb4]/20 text-[#0f172a]' },
  full: { label: 'Full', cls: 'bg-amber-100 text-amber-800' },
  canceled: { label: 'Canceled', cls: 'bg-slate-200 text-slate-700' },
  expired: { label: 'Expired', cls: 'bg-slate-200 text-slate-700' },
};

function formatWhen(iso) {
  if (!iso) return 'TBD';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

export default function GameRequestPage() {
  const { requestId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [gr, setGr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/game-requests/${requestId}`);
      setGr(data.request);
    } catch (err) {
      if (err.response?.status === 404) setGr({ _notFound: true });
      else toast.error('Failed to load request');
    } finally { setLoading(false); }
  }, [requestId]);

  useEffect(() => { load(); }, [load]);

  // Surface magic-link redirect state via ?rsvp=...
  useEffect(() => {
    const rsvp = searchParams.get('rsvp');
    if (!rsvp) return;
    const position = searchParams.get('position');
    const messages = {
      confirmed: position
        ? `You're confirmed for seat #${position}! 🎉`
        : "You're confirmed! 🎉",
      waitlist: position
        ? `Seats were full — you're #${position} on the waitlist. We'll email you if a spot opens.`
        : "You're on the waitlist. We'll email you if a spot opens.",
      invalid: 'That RSVP link is invalid or has expired.',
      notfound: 'That game request no longer exists.',
      self: "You can't RSVP to your own game request.",
      canceled: 'The host canceled this game request.',
      expired: 'This game request expired before you could RSVP.',
      full: 'Seats filled and the request closed before your RSVP landed.',
    };
    const msg = messages[rsvp];
    if (msg) {
      if (rsvp === 'confirmed' || rsvp === 'waitlist') toast.success(msg);
      else toast.error(msg);
    }
    // Clean the query so a refresh doesn't re-fire the toast.
    const next = new URLSearchParams(searchParams);
    next.delete('rsvp'); next.delete('position');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const rsvp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/game-requests/${requestId}/rsvp`);
      if (data.state === 'confirmed') toast.success(`You're confirmed for seat #${data.position}! 🎉`);
      else toast.success(`You're #${data.position} on the waitlist.`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to RSVP');
    } finally { setBusy(false); }
  };

  const unRsvp = async () => {
    setBusy(true);
    try {
      await api.delete(`/game-requests/${requestId}/rsvp`);
      toast.success('RSVP removed');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel RSVP');
    } finally { setBusy(false); }
  };

  const kick = async (userId, name) => {
    if (!window.confirm(`Remove ${name} from the roster?`)) return;
    setBusy(true);
    try {
      await api.delete(`/game-requests/${requestId}/responders/${userId}`);
      toast.success(`${name} removed`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove player');
    } finally { setBusy(false); }
  };

  const cancelRequest = async () => {
    if (!window.confirm('Cancel this game request? All confirmed and waitlist players will be emailed.')) return;
    setBusy(true);
    try {
      await api.post(`/game-requests/${requestId}/cancel`);
      toast.success('Game request canceled');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    } finally { setBusy(false); }
  };

  const share = async () => {
    const url = window.location.origin + `/game-requests/${requestId}`;
    try {
      if (navigator.share) await navigator.share({ title: 'Mahjong Game Request', url });
      else { await navigator.clipboard.writeText(url); toast.success('Link copied'); }
    } catch {/* user canceled */}
  };

  if (loading) {
    return (
      <div data-testid="game-request-loading" className="max-w-3xl mx-auto px-4 py-16 text-center text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin inline" />
      </div>
    );
  }

  if (gr?._notFound) {
    return (
      <div data-testid="game-request-not-found" className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Game request not found</h1>
        <p className="text-slate-500 mb-6">It may have been canceled or never existed.</p>
        <Link to="/find-players"><Button className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3fc99c]">Find players</Button></Link>
      </div>
    );
  }

  const isHost = gr.viewer_is_host;
  const isLoggedIn = !!user;
  const myEntry = isLoggedIn ? (gr.responders || []).find(r => r.user_id === user.id) : null;
  const confirmed = (gr.responders || []).filter(r => r.state === 'confirmed');
  const waitlist = (gr.responders || []).filter(r => r.state === 'waitlist');
  const seatsOpen = gr.seats_needed - confirmed.length;
  const closed = gr.status === 'canceled' || gr.status === 'expired';
  const badge = STATUS_BADGE[gr.status] || STATUS_BADGE.open;

  return (
    <div data-testid="game-request-page" className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-2xl bg-[#df5681]/15 flex items-center justify-center flex-shrink-0">
          <Zap className="w-6 h-6 text-[#df5681]" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Last-minute Mahjong game
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Hosted by <strong>{gr.host_name}</strong></p>
        </div>
        <Badge className={`rounded-full px-3 py-1 ${badge.cls}`} data-testid="game-request-status">{badge.label}</Badge>
      </div>

      <Card className="border-slate-100 mb-6">
        <CardContent className="p-5 sm:p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex items-start gap-2">
              <Users className="w-4 h-4 text-slate-400 mt-1" />
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-400">Seats needed</p>
                <p className="font-medium" data-testid="game-request-seats">
                  {confirmed.length} / {gr.seats_needed} filled
                  {seatsOpen > 0 && !closed && <span className="text-[#df5681] ml-1">· {seatsOpen} open</span>}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Clock className="w-4 h-4 text-slate-400 mt-1" />
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-400">When</p>
                <p className="font-medium" data-testid="game-request-when">{formatWhen(gr.game_when)}</p>
              </div>
            </div>
            {gr.where_text ? (
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-slate-400 mt-1" />
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-400">Where</p>
                  <p className="font-medium" data-testid="game-request-where">{gr.where_text}</p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-slate-400 mt-1" />
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-400">Where</p>
                  <p className="font-medium text-slate-400">Host will message confirmed players</p>
                </div>
              </div>
            )}
          </div>

          {gr.message && (
            <div className="bg-slate-50 rounded-xl p-3 border-l-2 border-[#56dfb4]" data-testid="game-request-message">
              <div className="flex items-start gap-2">
                <MessageSquare className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-slate-700 italic">{gr.message}</p>
              </div>
            </div>
          )}

          {/* Action area */}
          {!closed && (
            <div className="pt-2 flex flex-wrap gap-2">
              {isHost ? (
                <>
                  <Button onClick={share} variant="outline" className="rounded-full gap-2" data-testid="game-request-share-btn">
                    <Share2 className="w-4 h-4" /> Share link
                  </Button>
                  <Button onClick={cancelRequest} disabled={busy} variant="outline" className="rounded-full gap-2 text-red-600 border-red-200 hover:bg-red-50" data-testid="game-request-cancel-btn">
                    <XCircle className="w-4 h-4" /> Cancel request
                  </Button>
                </>
              ) : !isLoggedIn ? (
                <Link to={`/login?next=/game-requests/${requestId}`}>
                  <Button className="rounded-full bg-[#df5681] text-white hover:bg-[#c44570]" data-testid="game-request-login-cta">
                    Log in to RSVP
                  </Button>
                </Link>
              ) : myEntry ? (
                <>
                  <Badge className="rounded-full px-3 py-1.5 bg-[#56dfb4]/20 text-slate-900" data-testid="game-request-my-state">
                    <Check className="w-3.5 h-3.5 inline mr-1" />
                    {myEntry.state === 'confirmed' ? `You're confirmed (seat #${myEntry.position})` : `You're #${myEntry.position} on the waitlist`}
                  </Badge>
                  <Button onClick={unRsvp} disabled={busy} variant="outline" className="rounded-full" data-testid="game-request-unrsvp-btn">
                    Cancel my RSVP
                  </Button>
                </>
              ) : (
                <Button onClick={rsvp} disabled={busy} className="rounded-full bg-[#df5681] text-white hover:bg-[#c44570] gap-2" data-testid="game-request-rsvp-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  {gr.status === 'full' ? 'Join waitlist' : "I'm in"}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Roster */}
      <Card className="border-slate-100 mb-6">
        <CardContent className="p-5 sm:p-6">
          <h2 className="text-lg font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Confirmed roster ({confirmed.length}/{gr.seats_needed})
          </h2>
          {confirmed.length === 0 ? (
            <p className="text-sm text-slate-400 italic">No confirmed players yet — be the first!</p>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="game-request-confirmed-list">
              {confirmed.map(r => (
                <li key={r.user_id} className="py-2.5 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">{r.display_name}</p>
                    <p className="text-xs text-slate-400">
                      {(r.skill_level || 'unknown').charAt(0).toUpperCase() + (r.skill_level || 'unknown').slice(1)}
                      {r.distance_miles != null && ` · ~${r.distance_miles} mi`}
                    </p>
                  </div>
                  {isHost && !closed && (
                    <Button size="sm" variant="ghost" onClick={() => kick(r.user_id, r.display_name)} className="rounded-full text-red-600 hover:bg-red-50 gap-1" data-testid={`game-request-kick-${r.user_id}`}>
                      <UserMinus className="w-3.5 h-3.5" /> Remove
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {waitlist.length > 0 && (
            <>
              <h3 className="text-sm font-semibold text-slate-500 mt-5 mb-2 uppercase tracking-wider">Waitlist</h3>
              <ul className="divide-y divide-slate-100" data-testid="game-request-waitlist-list">
                {waitlist.map(r => (
                  <li key={r.user_id} className="py-2 flex items-center justify-between text-sm">
                    <span className="text-slate-700">#{r.position} {r.display_name}</span>
                    {isHost && !closed && (
                      <Button size="sm" variant="ghost" onClick={() => kick(r.user_id, r.display_name)} className="rounded-full text-red-600 hover:bg-red-50" data-testid={`game-request-kick-${r.user_id}`}>
                        <UserMinus className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </CardContent>
      </Card>

      {closed && (
        <div className="text-center py-4 text-sm text-slate-500" data-testid="game-request-closed-banner">
          This request is {gr.status}. <Link to="/find-players" className="text-[#df5681] underline">Find other players →</Link>
        </div>
      )}
    </div>
  );
}
